import { DROP_BAG_COUNTS, PHASE_TRIGGER_STAGES } from '../../config/gameplay';
import type { ResonancePhase } from '../simulation/dropBag';

export type GameModeId = 'classic' | 'five-resonance' | 'three-resonance';
export const DEFAULT_GAME_MODE: GameModeId = 'classic';
export type ModePhase = Readonly<{ firstStage: number; goalStage: number }>;
export type GameMode = Readonly<{
  id: GameModeId; name: string; caption: string; description: string;
  phases: readonly ModePhase[]; dropCounts: readonly number[]; carryMilestone: boolean;
}>;

export const GAME_MODES: readonly GameMode[] = [
  {
    id: 'classic', name: '通常', caption: 'いつもの五共鳴',
    description: '到達した石を次の共鳴へ持ち越す、これまでのルール。',
    phases: [...PHASE_TRIGGER_STAGES, 30].map((goalStage, index) => ({ firstStage: index * 5 + 1, goalStage })),
    dropCounts: DROP_BAG_COUNTS, carryMilestone: true,
  },
  {
    id: 'five-resonance', name: '五共鳴・短編', caption: '6段階 × 5共鳴',
    description: '3種類の石から6段階目へ。共鳴ごとに盤面を空にし、新しいキャラで進む。',
    phases: Array.from({ length: 5 }, (_, index) => ({ firstStage: index * 6 + 1, goalStage: (index + 1) * 6 })),
    dropCounts: [7, 5, 4], carryMilestone: false,
  },
  {
    id: 'three-resonance', name: '三共鳴・独立', caption: '10段階 × 3共鳴',
    description: '5種類の石から10段階目へ。共鳴ごとに盤面を空にし、三つの区間を踏破する。',
    phases: Array.from({ length: 3 }, (_, index) => ({ firstStage: index * 10 + 1, goalStage: (index + 1) * 10 })),
    dropCounts: DROP_BAG_COUNTS, carryMilestone: false,
  },
];

export function getGameMode(id: GameModeId = DEFAULT_GAME_MODE): GameMode {
  const mode = GAME_MODES.find(mode => mode.id === id);
  if (!mode) throw new Error(`Unknown game mode: ${id}`);
  return mode;
}

export function getModePhase(id: GameModeId, phase: number): ModePhase {
  const rules = getGameMode(id).phases[phase - 1];
  if (!rules) throw new Error(`Invalid phase ${phase} for ${id}`);
  return rules;
}

export function phaseName(id: GameModeId, phase: number): string {
  return phase === getGameMode(id).phases.length ? '最終共鳴' : `第${['一', '二', '三', '四', '五'][phase - 1]}共鳴`;
}

/** Three chapters use the opening, middle and final visual/audio palettes. */
export function presentationPhase(id: GameModeId, phase: ResonancePhase): ResonancePhase {
  return id === 'three-resonance' ? ([1, 3, 5] as const)[phase - 1]! : phase;
}
